import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SiteFooter from "@/components/SiteFooter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface BlogPostItem {
  slug: string;
  title: string;
  excerpt: string | null;
  category_name: string | null;
  created_at: string;
  featured_image: string | null;
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPostItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('slug, title, excerpt, created_at, featured_image, blog_categories(name)')
          .eq('status', 'published')
          .order('created_at', { ascending: false });
        if (error) throw error;
        setPosts((data || []).map((p: any) => ({
          slug: p.slug, title: p.title, excerpt: p.excerpt,
          category_name: p.blog_categories?.name || null,
          created_at: p.created_at, featured_image: p.featured_image,
        })));
      } catch {
        setPosts([]);
      } finally { setLoading(false); }
    };
    fetchPosts();
  }, []);

  return (
    <>
      <PageSEO title="ACG Blog | Air Conditioning Tips & Guides | Southern Highlands" description="Read expert tips, buying guides and industry news about air conditioning in the Southern Highlands. Stay informed with the ACG blog." />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Blog" }]} />
      <main>
        <section className="bg-acg-charcoal py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-primary-foreground mb-4">ACG Blog</h1>
            <p className="text-lg text-primary-foreground/70">Tips, guides, and news about air conditioning</p>
          </div>
        </section>

        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">No blog posts yet. Check back soon!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {posts.map(post => (
                  <Card key={post.slug} className="hover:shadow-md transition-shadow overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        {post.category_name && <Badge variant="outline">{post.category_name}</Badge>}
                        <span className="text-sm text-muted-foreground">
                          {new Date(post.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      </div>
                      <Link to={`/blog/${post.slug}`} className="group">
                        <h2 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">{post.title}</h2>
                      </Link>
                      {post.excerpt && <p className="text-muted-foreground mb-4">{post.excerpt}</p>}
                      <Link to={`/blog/${post.slug}`} className="text-primary font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
                        Read More <ArrowRight className="h-4 w-4" />
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
